import type { ProjectReadiness } from "@/api/hooks";
import type { ClinicianPublicationState } from "@/api/publication";

/**
 * The two status vocabularies a Project's status is told in, in one place
 * (#439).
 *
 * The Projects index renders two badges from the server projection —
 * `ProjectListItem.readiness` and `ProjectListItem.publicationStatus.state` —
 * and the Project page renders the second one again. Both live here so there is
 * exactly ONE table per contract enum: `ProjectListItem.publicationStatus`
 * exists precisely "so an index row and the page it opens can never disagree",
 * and two label maps for one enum is how they would disagree in words while
 * agreeing in data.
 *
 * ## An exhaustive switch, not a lookup
 *
 * `labels.ts` writes its maps `satisfies Record<TheUnion, string>` and explains
 * why: a new contract member becomes a compile error rather than a raw slug on
 * a clinician's screen. The switches below keep that guarantee — the
 * `never`-typed argument in each `default` arm is what a new member fails to
 * satisfy — and add the runtime half that `labels.ts` leaves to its call sites:
 * generated types describe a contract, they do not validate a response, so a
 * value this build has never heard of renders as itself instead of as a blank
 * cell.
 *
 * ## Nothing here is derived
 *
 * Each function maps ONE member of ONE projected enum to ONE string. No label
 * is composed from a second field, no state is inferred from a timestamp, and
 * the two axes are never combined into a third reading. `ProjectListItem`'s own
 * description is the rule: "a client that inferred readiness from a timestamp,
 * or attention from a combination of other fields, would be a second
 * implementation of a policy that has exactly one authoritative home".
 */

/**
 * The rung a Project's authoring has REACHED, in a clinician's words.
 *
 * Two properties of `ProjectReadiness` shape every label below, and both come
 * from the contract rather than from a reading of the mock:
 *
 *  - **It never reports a failure.** `draft_invalid` is "the honest position of
 *    a Project whose only content is a draft that has not passed validation —
 *    the rung it has reached", not a verdict on the clinician's work. So it
 *    reads "Not validated", a position, and never "Invalid" or "Failed"; the
 *    fact that somebody must fix it is `attentionReason`, an axis this surface
 *    deliberately does not render.
 *  - **It carries no acceptance and no deployment.** The furthest rung,
 *    `evidence_ready`, means a frozen candidate exists whose validation profile
 *    carries a verified engine identity (`candidates.evaluate_eligibility`) —
 *    it is reachable with nobody having accepted anything, and it says nothing
 *    about a release.
 *
 * THE CANONICAL REFERENCE LABELS THE TOP RUNG "Accepted", AND THIS DOES NOT.
 * Read at 1280 on 2026-08-26, the reference's two rows carrying publication
 * activity show a solid "Accepted" where the three without it show "Validated".
 * "Validated" is `draft_validated` and is used verbatim. "Accepted" is refused
 * on this axis: clinician acceptance is `clinician_signoffs`, projected as
 * `ClinicianPublicationState.accepted`, and a Project that froze an
 * evidence-eligible candidate nobody has accepted would render a solid
 * "Accepted" beside no second badge at all — an acceptance claim minted by the
 * browser out of a field that does not carry one. #439's own canonical-conflict
 * rule is to fail closed, so the word appears where the projection actually
 * carries it, one badge to the right.
 */
export function readinessLabel(readiness: ProjectReadiness): string {
  switch (readiness) {
    case "not_started":
      return "Not started";
    case "draft_in_progress":
      return "In progress";
    case "draft_invalid":
      return "Not validated";
    case "draft_validated":
      return "Validated";
    case "preview_ready":
      return "Preview ready";
    case "evidence_ready":
      return "Evidence ready";
    default:
      return unrecognised(readiness);
  }
}

/**
 * The compact clinician publication status (#267), as one word or phrase.
 *
 * These four strings are the Project page's, moved here unchanged rather than
 * restated: `ProjectStatus`'s badge and an index row read the SAME projected
 * value, and the contract's reason for embedding it on both — "so an index row
 * and the page it opens can never disagree" — is only real while one table
 * serves both.
 *
 * THE CANONICAL REFERENCE'S TWO SUBTLE BADGES ARE NOT IMPLEMENTED, and the
 * reason is the projection itself. It shows "Waiting for a manager" on one row
 * and "Handed to DevOps" on another; both are rungs of a publication that has
 * already been requested — a manager approval observed in GitHub, and the
 * post-merge promotion handoff — and `ClinicianPublicationState` cannot tell
 * them apart, because it is closed at four members and
 * `publication_requested` "covers EVERY rung a live publication can be on,
 * including merged, published and handed off … a refusal to mint a
 * clinician-facing claim about a release the Editor never observes". Splitting
 * them here would either re-derive that refusal in the browser or reach for
 * `GET .../git-lifecycle`, which #268 owns and #439 does not authorise. So the
 * distinction fails closed, exactly like the reference's `Preview target
 * required` row, and #448 owns annotating the mock.
 */
export function publicationStateLabel(state: ClinicianPublicationState): string {
  switch (state) {
    case "none":
      return "Not started";
    case "accepted":
      return "Accepted";
    case "publication_requested":
      return "Publication requested";
    // Rendered like the other three and paired, on the Project page, with the
    // recovery sentence its `reason` selects. The index renders no recovery at
    // all: `attentionReason` and `nextAction` are removed from that surface by
    // #439, and a badge that named a failure without its recovery would be the
    // dead end the Project page exists to avoid.
    case "action_needed":
      return "Needs your attention";
    default:
      return unrecognised(state);
  }
}

/**
 * The index's SECOND badge, or `null` when there is no second badge.
 *
 * `none` is omitted entirely rather than rendered as "Not started". It is a
 * real state and not an absence — #266 settled that for the projection itself —
 * but on a list it is also the commonest one, and a column where most rows
 * carry a badge saying nothing has happened is a column that reads as noise on
 * the rows where something has. The Project page, which shows ONE project, does
 * render it: there the sentence is the whole of what the reader came for.
 *
 * A state this build does not recognise is NOT omitted. Hiding an unknown
 * status is the fail-open direction — the row would look like a Project with no
 * publication activity — so it renders as itself and is visibly odd instead.
 */
export function publicationBadgeLabel(
  state: ClinicianPublicationState,
): string | null {
  return state === "none" ? null : publicationStateLabel(state);
}

/**
 * A member neither table knows, rendered as itself.
 *
 * The parameter is typed `never`, so adding a member to either contract enum
 * fails to compile HERE — at the table that would otherwise have gone silently
 * stale — which is the whole reason the tables are switches. The cast is what
 * lets the same function still answer at runtime for a response this build was
 * not compiled against.
 */
function unrecognised(member: never): string {
  return String(member);
}
